import {
  canSlocOn,
  slocDesksOf,
  type Permission,
  type SlocDesk,
} from "@nexseed/shared";
import { useAuth } from "@/features/auth/store";
import { useFacilities } from "@/features/auth/use-facilities";
import type { WarehouseStage } from "./api";
import { deskOpen, STAGES, type DeskArea } from "./labels";

/**
 * Every Warehouse route's guard: the permission, a desk the login works, and a desk that is open.
 * Condition and Pack send the reader to the Raw desk until their phase; SLOC Updation (`area`) is
 * open on all three. A desk the login does not hold sends it to the SLOC chooser (which lists only
 * its desks) or home.
 */
export function deskBlock(
  stage: string,
  area?: DeskArea
): "home" | "raw" | "sloc" | null {
  const { can } = useAuth.getState();
  if (!can("warehouse.view")) return "home";
  const s = STAGES.find((x) => x.stage === stage);
  if (s && !can(s.permission)) return area === "sloc" ? "sloc" : "home";
  return !s || !deskOpen(s, area) ? "raw" : null;
}

/**
 * The desks this login works, in SeedTrac's order: the desk lock, or for SLOC Updation (`area`
 * "sloc") its desk toggles, "RSW SLOC Updation" … (user, 2026-10-13).
 */
export function useHeldDesks(area?: DeskArea) {
  const perms = useAuth((s) => s.me?.permissions);
  const has = (c: Permission) => perms?.includes(c) ?? false;
  return STAGES.filter((s) =>
    area === "sloc" ? canSlocOn(has, s.stage) : has(s.permission)
  );
}

/**
 * SLOC Updation's desk to send this login to, else null to stay (user, 2026-10-13: a login with
 * one desk toggle opens that desk directly, as if it had clicked RSW in the chooser):
 * - the chooser (no desk asked) with exactly one desk: that desk;
 * - a desk it holds no toggle for: its first desk;
 * - a desk it holds, or the chooser with several desks: stay.
 */
export function slocDeskRedirect(stage: string | undefined): SlocDesk | null {
  const desks = slocDesksOf(useAuth.getState().can);
  if (!desks.length || desks.some((d) => d === stage)) return null;
  if (stage === undefined && desks.length > 1) return null;
  return desks[0]!;
}

/**
 * The plant this desk works at: the header's pick, or a one-plant login's only plant. Read from the
 * store, not `useFacilities().facilityId`: that stays null until /me/facilities loads, so the first
 * render's queries went out without a plant and the API refused them (400 facility_required).
 * `useFacilities()` still runs so a remembered plant that is no longer ours is dropped.
 */
export function useDeskFacility(): number | undefined {
  const me = useAuth((s) => s.me);
  const stored = useAuth((s) => s.facilityId);
  useFacilities();
  const sole = me?.facilityIds?.length === 1 ? me.facilityIds[0] : undefined;
  return stored ?? sole ?? undefined;
}

export const asStage = (s: string) => s as WarehouseStage;
